/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Labor hour absorption pricing method.
 * All overhead is loaded onto labor hours; materials/subs/equipment get flat markup.
 */

import type { BudgetRates } from "./estimates-types";
import type { LineItemPricingResult } from "./estimates-line-item-pricing";
import { safeDivide } from "./estimates-calculations-helpers";

/**
 * Labor hour absorption pricing: all overhead is loaded onto labor hours.
 * - Labor items: (burdened_wage + overhead_per_hour) / (1 - profit_margin)
 * - Material items: cost x (1 + material_markup)
 * - Equipment items: cost x (1 + equipment_markup)
 * - Subcontractor items: cost x (1 + sub_markup)
 * - Mixed items: overhead on labor portion, flat markup on rest
 */
export function calculateLaborHourAbsorptionPricing(
  unitCost: number,
  totalCost: number,
  adjustedTotalCost: number,
  laborCost: number,
  materialCost: number,
  equipmentCost: number,
  subcontractorCost: number,
  budgetRates: BudgetRates,
  profitPercentageOverride?: number,
): LineItemPricingResult {
  const profitMargin = budgetRates.profitMargin ?? 0.1;
  const overheadPerHour = budgetRates.overheadPerLaborHour ?? 0;
  const materialMarkup = budgetRates.materialMarkupRate ?? 0.15;
  const subMarkup = budgetRates.subcontractorMarkupRate ?? 0.1;
  const equipmentMarkup = budgetRates.equipmentMarkupRate ?? 0;

  let totalPrice: number;

  if (
    laborCost > 0 &&
    materialCost === 0 &&
    equipmentCost === 0 &&
    subcontractorCost === 0
  ) {
    // Pure labor line item: add overhead per hour, then margin
    const overheadRatio = unitCost > 0 ? overheadPerHour / unitCost : 0;
    const overhead = adjustedTotalCost * overheadRatio;
    const baseForMargin = adjustedTotalCost + overhead;
    const profitRate =
      profitPercentageOverride !== undefined
        ? profitPercentageOverride / 100
        : profitMargin;
    totalPrice =
      profitRate < 1
        ? safeDivide(baseForMargin, 1 - profitRate, baseForMargin)
        : baseForMargin;
  } else if (laborCost === 0) {
    // Non-labor line item: flat markup, no overhead loading
    const markup =
      materialCost > 0
        ? materialMarkup
        : subcontractorCost > 0
          ? subMarkup
          : equipmentCost > 0
            ? equipmentMarkup
            : materialMarkup;
    totalPrice = adjustedTotalCost * (1 + markup);
  } else {
    // Mixed line item: overhead on labor portion, flat markup on rest
    const costTotal =
      laborCost + materialCost + equipmentCost + subcontractorCost;
    const laborFraction = costTotal > 0 ? laborCost / costTotal : 0;
    const materialFraction = costTotal > 0 ? materialCost / costTotal : 0;
    const equipmentFraction = costTotal > 0 ? equipmentCost / costTotal : 0;
    const subFraction = costTotal > 0 ? subcontractorCost / costTotal : 0;

    const laborPortion = adjustedTotalCost * laborFraction;
    const materialPortion = adjustedTotalCost * materialFraction;
    const equipmentPortion = adjustedTotalCost * equipmentFraction;
    const subPortion = adjustedTotalCost * subFraction;
    const otherPortion =
      adjustedTotalCost -
      laborPortion -
      materialPortion -
      equipmentPortion -
      subPortion;

    const overheadRatio = unitCost > 0 ? overheadPerHour / unitCost : 0;
    const laborWithOverhead = laborPortion * (1 + overheadRatio);
    const materialWithMarkup = materialPortion * (1 + materialMarkup);
    const equipmentWithMarkup = equipmentPortion * (1 + equipmentMarkup);
    const subWithMarkup = subPortion * (1 + subMarkup);

    const baseForMargin =
      laborWithOverhead +
      materialWithMarkup +
      equipmentWithMarkup +
      subWithMarkup +
      otherPortion;
    const profitRate =
      profitPercentageOverride !== undefined
        ? profitPercentageOverride / 100
        : profitMargin;
    totalPrice =
      profitRate < 1
        ? safeDivide(baseForMargin, 1 - profitRate, baseForMargin)
        : baseForMargin;
  }

  const quantity = unitCost > 0 ? totalCost / unitCost : 1;
  let unitPrice: number;
  let profitPercentage: number;
  if (quantity > 0) {
    unitPrice = totalPrice / quantity;
    profitPercentage =
      totalPrice > 0
        ? Math.round(((totalPrice - adjustedTotalCost) / totalPrice) * 10000) /
          100
        : 0;
  } else if (unitCost > 0) {
    const profitRate =
      profitPercentageOverride !== undefined
        ? profitPercentageOverride / 100
        : profitMargin;
    const base = unitCost * (1 + overheadPerHour / Math.max(unitCost, 1));
    unitPrice = profitRate < 1 ? base / (1 - profitRate) : base;
    profitPercentage =
      unitPrice > 0
        ? Math.round(((unitPrice - unitCost) / unitPrice) * 10000) / 100
        : 0;
  } else {
    unitPrice = unitCost;
    profitPercentage = 0;
  }

  return { unitPrice, totalPrice, marginPercentage: 0, profitPercentage };
}
